import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dayCareApi } from '@/api/daycare';
import { billingApi } from '@/api/billing';
import { paymentApi } from '@/api/payment';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, FileDown, IndianRupee, Phone, Plus, Printer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { BillType, PaymentMethod } from '@/types/types';
import PaymentSplitsEditor, { getSplitsError, getSplitsTotal, PaymentSplitDraft } from '@/components/billing/PaymentSplitsEditor';
import LoyaltyBillingPanel from '@/components/billing/LoyaltyBillingPanel';
import { useCanMutateFrontDeskIpd } from '@/hooks/useFrontDeskBasePath';

function formatCurrency(n: number | undefined | null) {
  return `₹${Number(n ?? 0).toFixed(2)}`;
}

const ADD_ITEM_TYPES = [
  { value: 'DOCTOR_VISIT', label: 'Doctor Visit', itemType: BillType.OPD_CONSULTATION, description: 'Doctor Visit' },
  { value: 'INJECTIONS', label: 'Injections', itemType: BillType.MEDICINE, description: 'Injection' },
  { value: 'CONSUMABLES', label: 'Consumables', itemType: BillType.PROCEDURE, description: 'Consumables' },
  { value: 'PHARMA', label: 'Pharma', itemType: BillType.MEDICINE, description: 'Pharmacy' },
  { value: 'CUSTOM', label: 'Custom', itemType: BillType.PROCEDURE, description: '' },
] as const;

export default function DayCareBillingPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canMutateIpd = useCanMutateFrontDeskIpd();

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addType, setAddType] = useState<string>(ADD_ITEM_TYPES[0].value);
  const [addDescription, setAddDescription] = useState('');
  const [addQty, setAddQty] = useState(1);
  const [addRate, setAddRate] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editQty, setEditQty] = useState<string>('');
  const [editRate, setEditRate] = useState<string>('');
  const [editDescription, setEditDescription] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [splits, setSplits] = useState<PaymentSplitDraft[]>([{ mode: PaymentMethod.CASH, amount: '' }]);
  const [paymentNotes, setPaymentNotes] = useState('');
  const [discountPct, setDiscountPct] = useState('');
  const [discountAbs, setDiscountAbs] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['daycare-billing', caseId],
    enabled: !!caseId,
    queryFn: async () => {
      const res = await dayCareApi.getCaseById(caseId!);
      return res.data?.data;
    },
  });

  const c: any = data;
  const bill = c?.bill?.bill;
  const patient = c?.patient;
  const isDraft = bill?.status === 'DRAFT';
  const canEditBill = isDraft && canMutateIpd;

  const invalidateBill = () => {
    queryClient.invalidateQueries({ queryKey: ['daycare-billing', caseId] });
  };

  const updateItemMutation = useMutation({
    mutationFn: ({ itemId, data }: { itemId: string; data: { quantity?: number; unitPrice?: number; description?: string } }) =>
      billingApi.updateBillItem(bill!.id, itemId, data),
    onSuccess: () => {
      toast.success('Item updated');
      invalidateBill();
      setEditingItemId(null);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to update item'),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (itemId: string) => billingApi.deleteBillItem(bill!.id, itemId),
    onSuccess: () => {
      toast.success('Item removed');
      invalidateBill();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to remove item'),
  });

  const addItemMutation = useMutation({
    mutationFn: (payload: { itemType: BillType; description: string; quantity: number; unitPrice: number }) =>
      billingApi.addBillItem(bill!.id, {
        ...payload,
        discountAmount: 0,
      }),
    onSuccess: () => {
      toast.success('Item added');
      invalidateBill();
      setAddItemOpen(false);
      setAddDescription('');
      setAddQty(1);
      setAddRate('');
      setAddType(ADD_ITEM_TYPES[0].value);
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to add item'),
  });

  const processPaymentMutation = useMutation({
    mutationFn: (payments: { billId: string; amount: number; paymentMethod: PaymentMethod; notes?: string }[]) =>
      paymentApi.bulkProcessPayments(payments),
    onSuccess: () => {
      toast.success('Payment recorded');
      invalidateBill();
      setPaymentDialogOpen(false);
      setSplits([{ mode: PaymentMethod.CASH, amount: '' }]);
      setPaymentNotes('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to record payment'),
  });

  const paymentTotal = useMemo(() => getSplitsTotal(splits), [splits]);

  const updateDiscountMutation = useMutation({
    mutationFn: (data: { discountAmount?: number; discountPercentage?: number }) =>
      billingApi.updateBillDiscount(bill!.id, data),
    onSuccess: () => {
      toast.success('Discount applied');
      invalidateBill();
      setDiscountPct('');
      setDiscountAbs('');
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Failed to apply discount'),
  });

  const handleExportPDF = async () => {
    if (!bill?.id) {
      toast.error('No bill found to export');
      return;
    }
    try {
      const response = await billingApi.exportBillPDF(bill.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error: any) {
      console.error('Failed to export PDF', error);
      toast.error(error?.response?.data?.message || 'Failed to export PDF');
    }
  };

  const handlePrint = async () => {
    if (!bill?.id) return;
    const printWindow = window.open('', '_blank', 'width=800,height=600,left=100,top=100');
    if (!printWindow) {
      toast.error('Please allow popups to print the bill');
      return;
    }
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Loading...</title></head>
      <body style="display:flex;flex-direction:column;justify-content:center;align-items:center;height:100vh;margin:0;font-family:Arial;">
        <div style="border:4px solid #f3f3f3;border-top:4px solid #3498db;border-radius:50%;width:40px;height:40px;animation:spin 1s linear infinite;"></div>
        <p style="color:#666;margin-top:16px;">Loading bill for printing...</p>
      </body></html>
    `);
    printWindow.document.close();
    try {
      const response = await billingApi.exportBillPDF(bill.id, { letterhead: true });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      printWindow.location.href = url;
      const triggerPrint = () => {
        try {
          if (printWindow && !printWindow.closed) {
            printWindow.focus();
            printWindow.print();
          }
        } catch (e) {
          console.error('Print error:', e);
        }
      };
      setTimeout(triggerPrint, 1000);
      setTimeout(triggerPrint, 2500);
      printWindow.addEventListener('afterprint', () => {
        setTimeout(() => {
          if (printWindow && !printWindow.closed) printWindow.close();
          window.URL.revokeObjectURL(url);
        }, 100);
      });
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to load PDF for printing');
      printWindow.document.body.innerHTML = '<p style="padding:20px;color:red;">Failed to load PDF.</p>';
    }
  };

  const handleSendWhatsApp = async () => {
    if (!bill?.id || !patient?.phone) {
      toast.error('Missing patient phone or bill');
      return;
    }
    try {
      await api.post('/api/notifications/send-bill-whatsapp', {
        billId: bill.id,
      });
      toast.success('Bill sent via WhatsApp (if configured)');
    } catch (error: any) {
      console.error('Failed to send WhatsApp', error);
      toast.error(error?.response?.data?.message || 'Failed to send WhatsApp');
    }
  };

  const startEdit = (item: any) => {
    setEditingItemId(item.id);
    setEditQty(String(item.quantity));
    setEditRate(String(item.unitPrice));
    setEditDescription(item.description ?? '');
  };

  const saveEdit = () => {
    if (!editingItemId) return;
    const qty = parseInt(editQty, 10);
    const rate = parseFloat(editRate);
    if (isNaN(qty) || qty < 1 || isNaN(rate) || rate < 0) {
      toast.error('Invalid quantity or rate');
      return;
    }
    updateItemMutation.mutate({
      itemId: editingItemId,
      data: { quantity: qty, unitPrice: rate, description: editDescription || undefined },
    });
  };

  const handleAddItem = () => {
    const selected = ADD_ITEM_TYPES.find((t) => t.value === addType);
    if (!selected) return;
    const description = selected.value === 'CUSTOM' ? addDescription.trim() : (addDescription.trim() || selected.description);
    if (!description) {
      toast.error('Description is required');
      return;
    }
    const rate = parseFloat(addRate);
    if (isNaN(rate) || rate < 0) {
      toast.error('Enter a valid rate');
      return;
    }
    addItemMutation.mutate({
      itemType: selected.itemType,
      description,
      quantity: addQty,
      unitPrice: rate,
    });
  };

  const currentAddConfig = ADD_ITEM_TYPES.find((t) => t.value === addType);

  if (!caseId) {
    return <div className="p-6">Invalid Day Care case id</div>;
  }

  return (
    <div className="p-6 space-y-4">
      {!canMutateIpd ? (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
          View only — Operations Manager can review Day Care bills and export PDFs; editing items or recording payments requires reception or admin.
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">Day Care Bill</h1>
            {patient && (
              <p className="text-sm text-gray-600">
                {patient.name} · UHID: {patient.uhid || '—'} · {patient.phone}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileDown className="h-4 w-4 mr-1" />
            PDF
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint} title="Print bill (same format as OPD)">
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleSendWhatsApp}>
            <Phone className="h-4 w-4 mr-1" />
            WhatsApp
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-6 text-gray-500">Loading Day Care bill...</div>
      ) : !bill ? (
        <div className="p-6 text-gray-500">
          No bill found for this Day Care case. Create it from the Day Care case page first.
        </div>
      ) : (
        <>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <IndianRupee className="h-4 w-4 text-emerald-600" />
                Summary
              </CardTitle>
              <div className="text-right text-sm">
                <div className="font-medium">{bill.billNumber}</div>
                <div className="text-xs text-gray-500">
                  Status: {bill.status ?? 'DRAFT'}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Total Amount</div>
                  <div className="font-semibold">{formatCurrency(bill.totalAmount)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Paid Amount</div>
                  <div className="font-semibold">{formatCurrency(bill.paidAmount)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Due Amount</div>
                  <div className="font-semibold">{formatCurrency(bill.dueAmount)}</div>
                </div>
              </div>
              {canEditBill && (
                <div className="mt-4 pt-4 border-t space-y-3">
                  <div className="text-sm font-medium text-gray-700">Discount (deducted from total)</div>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Discount %</Label>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.5}
                        value={discountPct}
                        onChange={(e) => setDiscountPct(e.target.value)}
                        placeholder="0"
                        className="w-24"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Discount (₹)</Label>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        value={discountAbs}
                        onChange={(e) => setDiscountAbs(e.target.value)}
                        placeholder="0"
                        className="w-28"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => {
                        if (discountPct !== '') {
                          const pct = parseFloat(discountPct);
                          if (!isNaN(pct) && pct >= 0 && pct <= 100) {
                            updateDiscountMutation.mutate({ discountPercentage: pct });
                            return;
                          }
                        }
                        if (discountAbs !== '') {
                          const amt = parseFloat(discountAbs);
                          if (!isNaN(amt) && amt >= 0) {
                            updateDiscountMutation.mutate({ discountAmount: amt });
                            return;
                          }
                        }
                        toast.error('Enter discount % or amount');
                      }}
                      disabled={updateDiscountMutation.isPending}
                    >
                      Apply discount
                    </Button>
                  </div>
                  {(bill as any).discountAmount > 0 && (
                    <p className="text-xs text-gray-500">
                      Current discount: {formatCurrency((bill as any).discountAmount)}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Bill Items</CardTitle>
              {canEditBill && (
                <Button size="sm" className="bg-blue-800 hover:bg-blue-700" onClick={() => setAddItemOpen(true)}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add Item
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/60">
                      <TableHead className="w-10 text-center">#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      {canEditBill && <TableHead className="w-[120px]">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bill.billItems?.length ? (
                      bill.billItems.map((item: any, idx: number) => (
                        <TableRow key={item.id}>
                          <TableCell className="text-center text-xs text-gray-500">
                            {idx + 1}
                          </TableCell>
                          <TableCell>
                            {editingItemId === item.id ? (
                              <Input
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                className="h-8 text-sm"
                                placeholder="Description"
                              />
                            ) : (
                              <>
                                <div className="text-sm font-medium text-gray-900">
                                  {item.description}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {item.itemType}
                                </div>
                              </>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {editingItemId === item.id ? (
                              <Input
                                type="number"
                                min={1}
                                value={editQty}
                                onChange={(e) => setEditQty(e.target.value)}
                                className="h-8 w-20 text-right text-sm inline-block"
                              />
                            ) : (
                              <span className="text-sm">{item.quantity}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {editingItemId === item.id ? (
                              <Input
                                type="number"
                                min={0}
                                step={0.01}
                                value={editRate}
                                onChange={(e) => setEditRate(e.target.value)}
                                className="h-8 w-24 text-right text-sm inline-block"
                              />
                            ) : (
                              <span className="text-sm">{formatCurrency(item.unitPrice)}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {formatCurrency(item.totalPrice)}
                          </TableCell>
                          {canEditBill && (
                            <TableCell>
                              {editingItemId === item.id ? (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="default" onClick={saveEdit} disabled={updateItemMutation.isPending}>
                                    Save
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingItemId(null)}>
                                    Cancel
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" onClick={() => startEdit(item)}>
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => deleteItemMutation.mutate(item.id)}
                                    disabled={deleteItemMutation.isPending}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={canEditBill ? 6 : 5} className="py-6 text-center text-sm text-artis-slate">
                          No items on this bill. {canEditBill && 'Click "Add Item" to add line items.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {canMutateIpd && bill && patient && (
            <LoyaltyBillingPanel
              bill={{ ...bill, patient }}
              purposeOverride="DAY_CARE_DISCOUNT"
              patientId={patient.id}
              onApplied={invalidateBill}
            />
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Payments</CardTitle>
              {canMutateIpd && bill.dueAmount > 0 && (
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setPaymentDialogOpen(true)}>
                  Record Payment
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50/60">
                      <TableHead>Date</TableHead>
                      <TableHead>Mode</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bill.payments?.length ? (
                      bill.payments.map((p: any) => (
                        <TableRow key={p.id}>
                          <TableCell className="text-sm">
                            {new Date(p.paymentDate).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-sm">{String(p.paymentMethod || '').replace(/_/g, ' ')}</TableCell>
                          <TableCell className="text-sm">{String(p.status || '').replace(/_/g, ' ')}</TableCell>
                          <TableCell className="text-right text-sm">
                            {formatCurrency(p.amount)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} className="py-6 text-center text-sm text-artis-slate">
                          No payments recorded yet. Amount due: {formatCurrency(bill.dueAmount)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Record Payment</DialogTitle>
                <DialogDescription className="sr-only">Enter amount and select mode of payment.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <PaymentSplitsEditor
                  splits={splits}
                  onChange={setSplits}
                  maxTotal={bill.dueAmount}
                  disabled={processPaymentMutation.isPending}
                />
                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Input value={paymentNotes} onChange={(e) => setPaymentNotes(e.target.value)} placeholder="Reference / notes" />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
                  <Button
                    disabled={processPaymentMutation.isPending || !!getSplitsError({ splits, maxTotal: bill.dueAmount })}
                    onClick={() => {
                      const err = getSplitsError({ splits, maxTotal: bill.dueAmount });
                      if (err) {
                        toast.error(err);
                        return;
                      }
                      processPaymentMutation.mutate(
                        splits.map((sp) => ({
                          billId: bill.id,
                          amount: Number(sp.amount),
                          paymentMethod: sp.mode,
                          notes: paymentNotes || undefined,
                        }))
                      );
                    }}
                  >
                    {processPaymentMutation.isPending ? 'Recording...' : `Record Payment (₹${paymentTotal.toFixed(2)})`}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </>
      )}

      <Dialog open={addItemOpen} onOpenChange={setAddItemOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Bill Item</DialogTitle>
            <DialogDescription className="sr-only">
              Choose item type and enter description, quantity and rate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={addType} onValueChange={(v) => {
                setAddType(v);
                const config = ADD_ITEM_TYPES.find((t) => t.value === v);
                setAddDescription(config?.description ?? '');
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADD_ITEM_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{currentAddConfig?.value === 'CUSTOM' ? 'Description (required)' : 'Description'}</Label>
              <Input
                value={addDescription}
                onChange={(e) => setAddDescription(e.target.value)}
                placeholder={currentAddConfig?.value === 'CUSTOM' ? 'Enter item description' : currentAddConfig?.description}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Qty</Label>
                <Input
                  type="number"
                  min={1}
                  value={addQty}
                  onChange={(e) => setAddQty(parseInt(e.target.value, 10) || 1)}
                />
              </div>
              <div className="space-y-2">
                <Label>Rate (₹)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={addRate}
                  onChange={(e) => setAddRate(e.target.value)}
                  placeholder="0"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAddItemOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddItem} disabled={addItemMutation.isPending}>
                {addItemMutation.isPending ? 'Adding...' : 'Add Item'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
